/**
 * PDFビューアーコンポーネント
 * マニュアルPDFを新しいタブで開く
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from './ui/ToastManager';
import { manualApiClient } from '../services/manualApiClient';

/**
 * スタイル定義
 */
const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '20px'
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    width: '95vw',
    maxWidth: '600px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
    overflow: 'hidden'
  },
  header: {
    padding: '16px 24px',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#f8f9fa'
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#333'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '4px 8px',
    color: '#666',
    borderRadius: '4px',
    transition: 'color 0.2s ease, background-color 0.2s ease'
  },
  closeButtonHover: {
    color: '#333',
    backgroundColor: '#e9ecef'
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    padding: '40px'
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #007bff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px'
  },
  loadingText: {
    color: '#666',
    fontSize: '16px'
  },
  errorContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    padding: '40px',
    textAlign: 'center'
  },
  errorText: {
    color: '#dc3545',
    fontSize: '16px',
    marginBottom: '20px'
  },
  retryButton: {
    padding: '8px 16px',
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s ease'
  },
  pdfListContainer: {
    flex: 1,
    overflow: 'auto',
    padding: '16px',
    maxHeight: '400px'
  },
  pdfList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  pdfItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    backgroundColor: '#f8f9fa',
    transition: 'background-color 0.2s ease'
  },
  pdfItemHover: {
    backgroundColor: '#e9ecef'
  },
  pdfInfo: {
    flex: 1
  },
  pdfFileName: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#333',
    marginBottom: '4px'
  },
  pdfFileSize: {
    fontSize: '14px',
    color: '#666'
  },
  openButton: {
    padding: '8px 16px',
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s ease',
    whiteSpace: 'nowrap'
  },
  openButtonHover: {
    backgroundColor: '#0056b3'
  },
  noFilesMessage: {
    textAlign: 'center',
    color: '#666',
    fontSize: '16px',
    padding: '40px'
  }
};

/**
 * ファイルサイズをフォーマットする関数
 */
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '不明';
  
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  
  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

/**
 * PDFビューアーコンポーネント
 */
const PdfViewer = ({ 
  isOpen, 
  onClose, 
  manualType, 
  title = 'マニュアル' 
}) => {
  const { getCsrfTokenFromCookie } = useAuth(); 
  const { showError, showSuccess } = useToast();
  
  // 状態管理
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfUrls, setPdfUrls] = useState([]);

  /**
   * コンポーネントのクリーンアップ
   */
  const resetState = () => {
    setLoading(false);
    setError(null);
    setPdfUrls([]);
  };

  /**
   * PDFデータを取得する関数
   */
  const fetchPdfData = async () => {
    if (!manualType) {
      setError('必要な情報が不足しています');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // CSRFトークンを取得
      let csrfToken;
      
      try {
        csrfToken = await getCsrfTokenFromCookie(true);
      } catch (csrfError) {
        console.error('[PDF VIEWER] CSRF token acquisition failed:', csrfError);
        setError('セキュリティトークンの取得に失敗しました。ページを再読み込みして再度お試しください。');
        return;
      }
      
      if (!csrfToken) {
        console.error('[PDF VIEWER] CSRF token is empty');
        setError('セキュリティトークンの取得に失敗しました。ページを再読み込みして再度お試しください。');
        return;
      }
      
      // CSRFトークンを使用してPDFデータを取得
      const response = await manualApiClient.getManualPdfUrls(manualType, csrfToken);
      
      // バックエンドのレスポンス構造に合わせて修正
      // 'pdfs' または 'urls' キーをチェック
      const pdfList = response.pdfs || response.urls;
      
      if (!pdfList || pdfList.length === 0) {
        setError('マニュアルファイルが見つかりません');
        return;
      }

      // urlsの場合、pdfUrlキーがurlになっているので変換
      const normalizedPdfList = pdfList.map(pdf => ({
        fileName: pdf.fileName,
        fileSize: pdf.fileSize,
        pdfUrl: pdf.pdfUrl || pdf.url,  // pdfUrlまたはurlを使用
        filePath: pdf.filePath || pdf.path
      }));

      setPdfUrls(normalizedPdfList);
      
    } catch (err) {
      console.error('[PDF VIEWER] Failed to fetch PDF data:', err);

      // エラーメッセージの判定
      const errorMessage = err.message || manualApiClient.getErrorMessage(err);

      let displayError = errorMessage;

      if (errorMessage.includes('CSRF') || errorMessage.includes('セキュリティ')) {
        displayError = 'セキュリティ検証エラーが発生しました。ページを再読み込みして再度お試しください。';
      }
      
      if (errorMessage.includes('認証')) {
        displayError = '認証エラーが発生しました。再度ログインしてください。';
      }
      
      setError(displayError);
    } finally {
      setLoading(false);
    }
  };

  /**
   * PDFを新しいタブで開く処理
   */
  const handleOpenPdf = (pdfUrl) => {
    try {
      // 新しいタブでPDFを開く
      const newWindow = window.open(pdfUrl, '_blank');
      
      // ポップアップブロックされた場合の対応
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        showError('ポップアップがブロックされました。ブラウザの設定を確認してください。');
      } else {
        showSuccess('新しいタブでマニュアルを開きました');
      }
    } catch (err) {
      console.error('[PDF VIEWER] Failed to open PDF:', err);
      showError('PDFの表示でエラーが発生しました');
    }
  };

  /**
   * モーダルを閉じる処理
   */
  const handleClose = () => {
    resetState();
    onClose();
  };

  /**
   * リトライ処理
   */
  const handleRetry = () => {
    fetchPdfData();
  };

  /**
   * モーダルが開いた時の処理
   */
  useEffect(() => {
    if (!isOpen) {
      resetState();
      return;
    }
    
    if (manualType) {
      fetchPdfData();
    }
    // 開いた時とマニュアル種別が変わった時だけ取得したいので、関数は依存に含めない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, manualType]);

  /**
   * ESCキーでモーダルを閉じる
   */
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // handleClose を依存に含めると毎描画でリスナーを張り替えるため除外する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // モーダルが開いていない場合は何も表示しない
  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* CSS アニメーション */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      
      <div style={styles.overlay} onClick={handleClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          {/* ヘッダー */}
          <div style={styles.header}>
            <h2 style={styles.title}>{title}</h2>
            <button
              style={styles.closeButton}
              onClick={handleClose}
              onMouseEnter={(e) => Object.assign(e.target.style, styles.closeButtonHover)}
              onMouseLeave={(e) => Object.assign(e.target.style, styles.closeButton)}
              aria-label="閉じる"
            >
              ×
            </button>
          </div>

          {/* コンテンツ */}
          <div style={styles.content}>
            {loading && (
              <div style={styles.loadingContainer}>
                <div style={styles.loadingSpinner}></div>
                <div style={styles.loadingText}>マニュアルを読み込み中...</div>
              </div>
            )}

            {error && (
              <div style={styles.errorContainer}>
                <div style={styles.errorText}>{error}</div>
                <button
                  style={styles.retryButton}
                  onClick={handleRetry}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
                >
                  再試行
                </button>
              </div>
            )}

            {/* PDF一覧表示 */}
            {pdfUrls.length > 0 && !loading && !error && (
              <div style={styles.pdfListContainer}>
                <div style={styles.pdfList}>
                  {pdfUrls.map((pdf, index) => (
                    <div
                      key={index}
                      style={styles.pdfItem}
                      onMouseEnter={(e) => Object.assign(e.currentTarget.style, styles.pdfItemHover)}
                      onMouseLeave={(e) => Object.assign(e.currentTarget.style, styles.pdfItem)}
                    >
                      <div style={styles.pdfInfo}>
                        <div style={styles.pdfFileName}>{pdf.fileName}</div>
                        {pdf.fileSize && (
                          <div style={styles.pdfFileSize}>
                            {`ファイルサイズ: ${formatFileSize(pdf.fileSize)}`}
                          </div>
                        )}
                      </div>
                      <button
                        style={styles.openButton}
                        onClick={() => handleOpenPdf(pdf.pdfUrl)}
                        onMouseEnter={(e) => Object.assign(e.target.style, styles.openButtonHover)}
                        onMouseLeave={(e) => Object.assign(e.target.style, styles.openButton)}
                      >
                        開く
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ファイルが見つからない場合 */}
            {pdfUrls.length === 0 && !loading && !error && (
              <div style={styles.noFilesMessage}>
                このマニュアルのファイルが見つかりません
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PdfViewer;